import type { ButtonHTMLAttributes } from 'react'
import { cn } from '@/shared/lib/cn'
import type { IconName } from '@/shared/ui/icon'
import { Icon } from '@/shared/ui/icon'
import s from './IconButton.module.css'

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: IconName
  title: string
  active?: boolean
  /**
   * Размер и толщина обводки иконки — БЕЗ дефолта намеренно.
   *
   * В макете у каждой иконки шапки свои значения, и они не совпадают ни между собой, ни с
   * каким-либо «общим» числом: колокол 17/1.8 (prototype/Prototype.dc.html:177), шестерёнка
   * 17/1.7 (строка 207), тема 16/1.8 (строка 210). ⚠️ Любой дефолт здесь — выдуманное число,
   * которое молча разъедется с макетом на всех трёх вызовах сразу. Поэтому размер задаёт
   * вызывающая сторона, у которой перед глазами строка макета.
   */
  size: number
  strokeWidth: number
  /**
   * Точка непрочитанного поверх иконки (колокол уведомлений в шапке,
   * prototype/Prototype.dc.html:178). Внутри кнопки, а не соседним элементом: `Popover`
   * (Task 16) вешает свой триггер через Radix `asChild` на САМ переданный узел — если бы точка
   * была снаружи в обёртке, `asChild` отдал бы интерактивность обёртке, а не кнопке.
   */
  dot?: boolean
}

export function IconButton({
  icon,
  title,
  active,
  size,
  strokeWidth,
  dot,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      title={title}
      aria-label={title}
      className={cn(s.root, active && s.active, className)}
      {...rest}
    >
      <Icon name={icon} size={size} strokeWidth={strokeWidth} />
      {dot && <span className={s.dot} aria-hidden="true" />}
    </button>
  )
}
